import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DATE_FORMAT } from "../utils/date-time-utility";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import Text from "./Text";

type DatePickerProps = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  onChange: (val: dayjs.Dayjs | null) => void;
  label?: string;
  value: any;
  flexprops?: FlexProps;
  error?: string | boolean;
  required?: boolean;
  marginBottom?: string;
  slots?: any;
  slotProps?: any;
  placeholder?: string;
  name?: string;
  handleBlur?: any;
  /**
   * The picker cannot be edited. Greyed out on an editable form; inside a
   * `ReadOnlyFields` region the date is presented instead.
   */
  disabled?: boolean;
  /** Alias of `disabled`, for call sites that read better as "read-only". */
  readOnly?: boolean;
  disableError?: boolean;
  /** Typed dates outside min/max are moved to the nearest bound. Set false to keep them and show an `error` instead. */
  clampToRange?: boolean;
};

const CustomDatePicker = (props: DatePickerProps) => {
  const lock = useFieldLock(props.disabled || props.readOnly);
  const clampToRange = props.clampToRange ?? true;

  return (
    <Flex
      {...props.flexprops}
      flexDirection={props.flexprops?.flexDirection ?? "column"}
      width={"100%"}
    >
      {/* <Typography mb={props.marginBottom ?? "10px"} sx={DEFAULT_FONTSIZE}>
        {props.label}
        {props.required && (
          <Typography fontSize={"13px"} component={"span"} color={"error"}>
            {` *`}
          </Typography>
        )}
      </Typography> */}
      <Flex flexDirection={"column"} width={"100%"} sx={{}}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            slots={{ ...(props.slots ?? {}) }}
            views={["year", "month", "day"]}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            slotProps={{
              popper: {
                disablePortal: false,
              },
              inputAdornment: {
                position: props.slotProps?.inputAdornment?.position || "start",
              },
              ...(props.slotProps ?? {}),
              textField: {
                InputLabelProps: { shrink: true },
                label: inputLabel(props?.label, props?.required),
                onBlur: props.handleBlur, // Pass Formik's handleBlur
                name: props.name, // Pass Formik's field name
                placeholder: props.placeholder ?? DATE_FORMAT,
                error: !!props.error,
              },
            }}
            minDate={props.minDate}
            maxDate={props.maxDate}
            sx={{
              "& .MuiInputAdornment-positionStart": {
                marginRight: "0px !important",
              },
              "& .MuiIconButton-root": {
                paddingRight: "0px !important",
              },
              "& .MuiIconButton-root .MuiSvgIcon-root": {
                fontSize: "10px !important",
              },
            }}
            // sx={{
            //   width: "100%",
            //   "& .MuiOutlinedInput-root": {
            //     border: props?.error ? "0.0001rem solid red" : undefined,
            //     borderColor: props.error ? "error.main" : undefined,
            //     "& fieldset": {
            //       borderColor: props.error ? "error.main" : undefined,
            //     },
            //     "&:hover fieldset": {
            //       borderColor: props.error ? "error.main" : "inherit",
            //     },
            //     "&.Mui-focused fieldset": {
            //       borderColor: props.error ? "error.main" : "primary.main",
            //     },
            //   },
            // }}
            format={DATE_FORMAT}
            value={props.value ? dayjs(props.value) : null}
            onChange={(val) => {
              if (!val) {
                props.onChange(val);
                return;
              }

              if (!clampToRange) {
                props.onChange(val);
                return;
              }

              let adjustedVal = val;

              // If the date is after maxDate, clamp it to maxDate
              if (props.maxDate && dayjs(val).isAfter(props.maxDate)) {
                adjustedVal = props.maxDate;
              }

              // If the date is before minDate, clamp it to minDate
              if (props?.minDate && dayjs(adjustedVal).isBefore(props.minDate))
                adjustedVal = props.minDate;

              props.onChange(adjustedVal);
            }}
          />
        </LocalizationProvider>

        {!!props?.error && !props.disableError && (
          <Text fontSize={"13px"} variant="caption" color="error">
            {props?.error}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default CustomDatePicker;
